import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useProjectStore } from '../../contexts/ProjectContext';
import { useProjects } from '../../hooks/useProjects';
import { useSettings } from '@/contexts/SettingsContext';
import { formatDateByPreference } from '@/utils/dateFormat';
import { projectApi } from '../../api/projectApi';
import UploadWizardDrawer from '../../components/upload/UploadWizardDrawer';
import { compareSchedules } from '../../api/schedule';
import { API_BASE_URL } from '../../config';
import { PanelLoading } from '@/components/ui/loading';

// Define schedule category type
type ScheduleCategory = 'Current' | 'Baseline' | 'Historical' | 'What-If';

export const Schedules: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { 
    projects, 
    currentProject: storeCurrentProject, 
    setCurrentProject, 
    refreshProjectSchedules,
    loading,
    error
  } = useProjectStore();
  const { addScheduleToProject } = useProjects();
  const { settings } = useSettings();

  const [activeCategory, setActiveCategory] = useState<ScheduleCategory>('Current');
  const [searchQuery, setSearchQuery] = useState('');
  const [isUploadDrawerOpen, setIsUploadDrawerOpen] = useState(false);
  const [selectedSchedules, setSelectedSchedules] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'newest' | 'name' | 'activities'>('newest');
  const [isComparing, setIsComparing] = useState(false);
  const [compareError, setCompareError] = useState<string | null>(null);
  
  // Get current project from URL param or store
  const currentProject = projectId 
    ? projects.find(p => p.id === projectId) || storeCurrentProject
    : storeCurrentProject;
  
  // Set current project in store if it's not already set
  useEffect(() => {
    if (currentProject && (!storeCurrentProject || storeCurrentProject.id !== currentProject.id)) {
      setCurrentProject(currentProject);
    }
  }, [currentProject, storeCurrentProject, setCurrentProject]);

  // Refresh project schedules when component mounts or projectId changes
  useEffect(() => {
    if (projectId) {
      console.log('Refreshing schedules for project:', projectId);
      refreshProjectSchedules(projectId);
    }
  }, [projectId, refreshProjectSchedules]);

  // Debug: Log current project and schedules whenever they change
  useEffect(() => {
    console.log('Current project updated:', currentProject);
    console.log('Current project schedules:', currentProject?.schedules);
    
    // Log detailed schedule information
    if (currentProject?.schedules) {
      currentProject.schedules.forEach((schedule, index) => {
        console.log(`Schedule ${index + 1} details:`, {
          id: schedule.id,
          name: schedule.name,
          status: schedule.status,
          activity_count: schedule.activity_count,
          start_date: schedule.start_date,
          end_date: schedule.end_date,
          duration: schedule.duration,
          created_at: schedule.created_at,
          updated_at: schedule.updated_at,
          xer_project_id: schedule.xer_project_id,
          upload_id: schedule.upload_id,
          description: schedule.description,
          version: schedule.version,
          relationship_count: schedule.relationship_count,
          data_date: schedule.data_date,
          schedule_metadata: schedule.schedule_metadata
        });
      });
    }
  }, [currentProject]);

  // Debug function to test backend API directly
  const testBackendAPI = async () => {
    if (!projectId) return;
    
    try {
      console.log('Testing backend API directly...');
      
      // Test the main schedules endpoint
      const response = await fetch(`${API_BASE_URL}/xer/dashboard`);
      const data = await response.json();
      console.log('Direct API response:', data);
      
      // Test the debug endpoint
      const debugResponse = await fetch(`${API_BASE_URL}/xer/project-summary/${projectId}`);
      const debugData = await debugResponse.json();
      console.log('Debug endpoint response:', debugData);
      
    } catch (error) {
      console.error('Error testing backend API:', error);
    }
  };

  // Test function to force mock data
  const testWithMockData = async () => {
    if (!projectId) return;
    
    try {
      console.log('Testing with mock data...');
      
      // Force the API to use mock data by causing an error
      const mockSchedules = await projectApi.getProjectSchedules(projectId + '_mock');
      
      // Update the current project with mock schedules
      if (currentProject) {
        const updatedProject = { ...currentProject, schedules: mockSchedules };
        setCurrentProject(updatedProject);
        console.log('Updated project with mock schedules:', updatedProject);
      }
      
    } catch (error) {
      console.error('Error testing with mock data:', error);
    }
  };

  // Test function for schedule comparison
  const testScheduleComparison = async () => {
    try {
      console.log('Testing schedule comparison...');
      
      // Use the schedule IDs from the current project
      const scheduleIds = currentProject?.schedules?.map(s => s.id) || [];
      
      if (scheduleIds.length < 2) {
        console.log('Need at least 2 schedules for comparison');
        return;
      }
      
      // Test the comparison API
      const comparisonData = await compareSchedules(scheduleIds);
      console.log('Comparison result:', comparisonData);
      
      // Show success message
      console.log('✅ Schedule comparison successful! Navigating to comparison page...');
      
      // Navigate to comparison page
      navigate(`/projects/${projectId}/schedules/compare`, {
        state: { comparisonData }
      });
      
    } catch (error) {
      console.error('Error testing schedule comparison:', error);
    }
  };

  // Call debug function when component mounts
  useEffect(() => {
    testBackendAPI();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- testBackendAPI recreated each render; projectId is key
  }, [projectId]);

  // Loading and error UI
  if (loading) {
    return <PanelLoading message="Loading schedules..." className="p-6" />;
  }
  if (error) {
    return <div className="p-6 text-center text-red-600">{error}</div>;
  }
  
  // Filter schedules based on active category and search query (case-insensitive)
  let filteredSchedules = currentProject && currentProject.schedules
    ? currentProject.schedules.filter(schedule => {
        // More flexible status matching
        const scheduleStatus = schedule.status?.toLowerCase() || '';
        const categoryStatus = activeCategory.toLowerCase();
        
        // Map backend statuses to frontend categories
        const statusMapping: { [key: string]: string[] } = {
          'current': ['current', 'ready', 'active'],
          'baseline': ['baseline', 'baseline'],
          'historical': ['historical', 'completed', 'archived'],
          'what-if': ['what-if', 'what_if', 'draft', 'scenario']
        };
        
        // Check if schedule status matches the active category
        const matchingStatuses = statusMapping[categoryStatus] || [categoryStatus];
        return matchingStatuses.includes(scheduleStatus);
      })
    : [];

  // If no schedules match the active category, show all schedules as fallback
  if (filteredSchedules.length === 0 && currentProject && currentProject.schedules) {
    filteredSchedules = currentProject.schedules;
  }
  
  // Debug: Log the filtering process
  console.log('Current project schedules:', currentProject?.schedules);
  console.log('Active category:', activeCategory);
  console.log('Filtered schedules:', filteredSchedules);
  console.log('Schedule statuses:', currentProject?.schedules?.map(s => s.status));
  
  // Sort schedules based on selected sort option
  const sortedSchedules = [...filteredSchedules].sort((a, b) => {
    if (sortBy === 'newest') {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    } else if (sortBy === 'name') {
      return a.name.localeCompare(b.name);
    } else if (sortBy === 'activities') {
      const aCount = a.activity_count || 0;
      const bCount = b.activity_count || 0;
      return bCount - aCount;
    }
    return 0;
  });
  
  // Get schedule status color
  const getStatusColor = (status: string | undefined): string => {
    switch (status) {
      case 'Current': return 'bg-green-100 text-green-800';
      case 'Baseline': return 'bg-blue-100 text-blue-800';
      case 'Historical': return 'bg-gray-100 text-gray-800';
      case 'What-If': return 'bg-purple-100 text-purple-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'UPLOADING': return 'bg-yellow-100 text-yellow-800';
      case 'ERROR': return 'bg-red-100 text-red-800';
      case 'ready': return 'bg-green-100 text-green-800';
      case 'processing': return 'bg-yellow-100 text-yellow-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Format status for display
  const formatStatus = (status: string | undefined): string => {
    if (!status) return 'N/A';
    
    switch (status.toLowerCase()) {
      case 'ready': return 'Ready';
      case 'processing': return 'Processing';
      case 'completed': return 'Completed';
      case 'uploading': return 'Uploading';
      case 'error': return 'Error';
      case 'draft': return 'Draft';
      case 'current': return 'Current';
      case 'baseline': return 'Baseline';
      case 'historical': return 'Historical';
      case 'what_if': return 'What-If';
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };
  
  // Handle upload schedule
  const handleUploadSchedule = () => {
    setIsUploadDrawerOpen(true);
  };
  
  // Handle closing the upload drawer
  const handleCloseUploadDrawer = () => {
    setIsUploadDrawerOpen(false);
  };
  
  // Handle schedule upload completion
  const handleScheduleUploaded = async (scheduleData: any) => {
    if (!currentProject) return;
    
    try {
      // Add the new schedule to the project - use actual data from backend, no fallbacks
      await addScheduleToProject(currentProject.id, {
        name: scheduleData.name,
        description: scheduleData.description,
        status: activeCategory, // Use the currently active category
        start_date: scheduleData.start_date,
        end_date: scheduleData.end_date,
        duration: scheduleData.duration,
        activity_count: scheduleData.activity_count
      });
      
      // Close the upload drawer
      setIsUploadDrawerOpen(false);
      
      // Show success message (in a real app, this would be a toast notification)
      console.log('Schedule uploaded successfully');
    } catch (error) {
      console.error('Failed to upload schedule:', error);
    }
  };
  
  // Handle compare schedules
  const handleCompareSchedules = async () => {
    if (selectedSchedules.length < 2) {
      setCompareError('Please select at least 2 schedules to compare');
      return;
    }
    
    setIsComparing(true);
    setCompareError(null);
    
    try {
      // Debug: Log the selected schedule IDs
      console.log('Selected schedule IDs:', selectedSchedules);
      console.log('Selected schedules data:', selectedSchedules.map(id => 
        currentProject?.schedules?.find(s => s.id === id)
      ));
      
      // Call the real API to compare schedules
      const comparisonData = await compareSchedules(selectedSchedules);
      
      // Navigate to comparison page with the data
      navigate(`/projects/${projectId}/schedules/compare`, {
        state: { 
          comparisonData,
          selectedSchedules: selectedSchedules.map(id => 
            currentProject?.schedules?.find(s => s.id === id)
          ).filter(Boolean)
        }
      });
      
    } catch (error) {
      console.error('Failed to compare schedules:', error);
      setCompareError('Failed to compare schedules. Please try again.');
    } finally {
      setIsComparing(false);
    }
  };
  
  // Handle schedule selection for comparison
  const handleToggleScheduleSelection = (scheduleId: string) => {
    console.log('Toggling schedule selection:', scheduleId);
    console.log('Current selectedSchedules:', selectedSchedules);
    
    setSelectedSchedules(prev => {
      const newSelection = prev.includes(scheduleId)
        ? prev.filter(id => id !== scheduleId)
        : [...prev, scheduleId];
      console.log('New selectedSchedules:', newSelection);
      return newSelection;
    });
  };

  // Get the primary ID for a schedule (id or upload_id as fallback)
  const getSchedulePrimaryId = (schedule: any): string | null => {
    return schedule.id || schedule.upload_id || null;
  };

  // Log the schedules for debugging
  console.log('Schedules to render:', sortedSchedules);
  console.log('Schedule IDs available:', sortedSchedules.map(s => ({ 
    id: s.id, 
    name: s.name, 
    upload_id: s.upload_id,
    hasId: !!s.id 
  })));
  console.log('Currently selected schedules:', selectedSchedules);
  console.log('Current project:', currentProject);
  console.log('Current project schedules:', currentProject?.schedules);

  // Only render schedules grid if currentProject is not null
  if (!currentProject) {
    return null;
  }

  return (
    <div className="p-6">
      {/* Breadcrumb navigation */}
      <div className="mb-4 flex items-center text-sm text-gray-500">
        <Link to="/projects" className="hover:text-primary">Projects</Link>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mx-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <Link to={`/projects/${projectId}`} className="hover:text-primary">{currentProject ? currentProject.name : ''}</Link>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mx-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className="font-medium text-gray-900">Schedules</span>
      </div>
      
      {/* Page header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">Project Schedules</h1>
            <p className="text-gray-500">
              Manage and compare schedules for {currentProject ? currentProject.name : ''}
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleCompareSchedules}
              disabled={selectedSchedules.length < 2 || isComparing}
              className={`px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md transition-colors text-sm flex items-center ${
                selectedSchedules.length < 2 || isComparing
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'hover:bg-gray-50'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              {isComparing ? 'Comparing...' : `Compare (${selectedSchedules.length})`}
            </button>
            <button
              onClick={handleUploadSchedule}
              className="px-4 py-1.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-md transition-colors flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              <span>Upload Schedule</span>
            </button>
          </div>
        </div>
      </div>

      {/* Debug Panel */}
      <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Debug Info:</h3>
        <div className="text-xs text-gray-600 space-y-1">
          <p>Project ID: {projectId}</p>
          <p>Current Project: {currentProject?.name}</p>
          <p>Total Schedules: {currentProject?.schedules?.length || 0}</p>
          <p>Filtered Schedules: {filteredSchedules.length}</p>
          <p>Selected Schedules: {selectedSchedules.length}</p>
          <p>Active Category: {activeCategory}</p>
          <button 
            onClick={testBackendAPI}
            className="mt-2 px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
          >
            Test Backend API
          </button>
          <button 
            onClick={testWithMockData}
            className="mt-2 ml-2 px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
          >
            Test Mock Data
          </button>
          <button 
            onClick={testScheduleComparison}
            className="mt-2 ml-2 px-3 py-1 bg-purple-500 text-white text-xs rounded hover:bg-purple-600"
          >
            Test Comparison
          </button>
        </div>
        <div className="mt-2">
          <p className="text-xs text-gray-500 mb-1">Schedule Data</p>
          <pre className="text-xs text-gray-600 bg-white p-2 rounded border overflow-auto max-h-32">
            {JSON.stringify(currentProject?.schedules || [], null, 2)}
          </pre>
        </div>
      </div>

      {/* Error message for comparison */}
      {compareError && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {compareError}
        </div>
      )}

      {/* Schedule category tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {(['Current', 'Baseline', 'Historical', 'What-If'] as ScheduleCategory[]).map(category => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                activeCategory === category 
                  ? 'border-primary text-primary' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {category}
            </button>
          ))}
        </nav>
      </div>

      {/* Search and filter */}
      <div className="mb-6 flex justify-between">
        <div className="relative w-64">
          <input 
            type="text" 
            placeholder="Search schedules..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
        <div className="flex space-x-2">
          <select 
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'newest' | 'name' | 'activities')}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="newest">Sort by: Newest</option>
            <option value="name">Sort by: Name</option>
            <option value="activities">Sort by: Activities</option>
          </select>
        </div>
      </div>
          
      {/* Schedules grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sortedSchedules.length > 0 ? (
          sortedSchedules.map((schedule, idx) => (
            <div 
              key={getSchedulePrimaryId(schedule) || idx}
              className={`bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-all hover:translate-y-[-1px] ${
                getSchedulePrimaryId(schedule) && selectedSchedules.includes(getSchedulePrimaryId(schedule)!)
                  ? 'border-primary' 
                  : 'border-gray-200'
              }`}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-2">
                  <h2 className="text-lg font-medium text-gray-800">{schedule.name || 'N/A'}</h2>
                  <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(schedule.status)}`}>
                    {schedule.status && schedule.status.toLowerCase() === 'current' ? 'Current' : formatStatus(schedule.status)}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-gray-500">Created</p>
                    <p className="text-sm text-gray-700">
                      {schedule.created_at ? 
                        formatDateByPreference(schedule.created_at, settings.dateFormat, { timeZone: settings.timezone }) : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Activities</p>
                    <p className="text-sm text-gray-700">{typeof schedule.activity_count === 'number' ? schedule.activity_count : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Start Date</p>
                    <p className="text-sm text-gray-700">
                      {schedule.start_date ? 
                        formatDateByPreference(schedule.start_date, settings.dateFormat, { timeZone: settings.timezone }) : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">End Date</p>
                    <p className="text-sm text-gray-700">
                      {schedule.end_date ? 
                        formatDateByPreference(schedule.end_date, settings.dateFormat, { timeZone: settings.timezone }) : 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="border-t border-gray-100 pt-4 mt-2 flex justify-between items-center">
                  <div className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-xs text-gray-500">
                      {typeof schedule.duration === 'number' ? `${schedule.duration} days` : 'Duration N/A'}
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => {
                        const primaryId = getSchedulePrimaryId(schedule);
                        if (primaryId) {
                          handleToggleScheduleSelection(primaryId);
                        } else {
                          console.warn('Schedule has no ID:', schedule);
                        }
                      }}
                      disabled={!getSchedulePrimaryId(schedule)}
                      className={`px-3 py-1 text-sm rounded ${
                        getSchedulePrimaryId(schedule) && selectedSchedules.includes(getSchedulePrimaryId(schedule)!)
                          ? 'bg-primary text-primary-foreground'
                          : getSchedulePrimaryId(schedule) 
                            ? 'text-gray-600 hover:text-gray-800'
                            : 'text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {getSchedulePrimaryId(schedule) && selectedSchedules.includes(getSchedulePrimaryId(schedule)!) ? 'Selected' : 'Compare'}
                    </button>
                    <Link 
                      to={currentProject && getSchedulePrimaryId(schedule) ? `/projects/${projectId}/schedules/${getSchedulePrimaryId(schedule)}` : '#'}
                      className={`px-3 py-1 text-sm rounded-md ${
                        getSchedulePrimaryId(schedule) 
                          ? 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                          : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      View
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-3 bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h2 className="text-xl font-medium text-gray-700 mb-2">No {activeCategory} Schedules Found</h2>
            <p className="text-gray-500 mb-6">
              {activeCategory === 'Current' && "Upload a schedule and set it as current to get started."}
              {activeCategory === 'Baseline' && "Set a schedule as baseline to track progress against it."}
              {activeCategory === 'Historical' && "Historical schedules will appear here when available."}
              {activeCategory === 'What-If' && "Create what-if scenarios to explore alternative approaches."}
            </p>
            <button 
              onClick={handleUploadSchedule}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-md transition-colors"
            >
              Upload Schedule
            </button>
          </div>
        )}
      </div>
      
      {/* Upload Wizard Drawer */}
      {isUploadDrawerOpen && (
        <UploadWizardDrawer
          isOpen={isUploadDrawerOpen}
          onClose={handleCloseUploadDrawer}
          projectId={currentProject.id}
          onUploadComplete={handleScheduleUploaded}
        />
      )}
    </div>
  );
};

export default Schedules; 